import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MapPin, ArrowLeft, CheckCircle } from 'lucide-react';
import { companyInfo, serviceAreas, services } from '../mock';

const AreaDetail = () => {
  const { id } = useParams();
  const area = serviceAreas.find(a => a.id === id);

  if (!area) {
    return (
      <div className="pt-32 pb-20 container text-center">
        <h1 className="heading-2 mb-4">Area Not Found</h1>
        <Link to="/areas" className="btn-primary">
          View All Service Areas
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <section className="pt-32 pb-8">
        <div className="container">
          <Link to="/areas" className="flex items-center gap-2 body-small" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={16} />
            Back to Service Areas
          </Link>
        </div>
      </section>

      {/* Hero Section */}
      <section className="pb-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="flex items-start gap-4 max-w-4xl">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
              <MapPin size={32} style={{ color: 'var(--accent-text)' }} />
            </div>
            <div>
              <h1 className="heading-1 mb-6">Pest Control in {area.name}</h1>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                {area.description} Fast, eco-friendly pest extermination with same-day service available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="space-y-8">
                <div>
                  <h2 className="heading-2 mb-4">Local Pest Control Experts in {area.name}</h2>
                  <p className="body-medium mb-4" style={{ color: 'var(--text-body)' }}>
                    At NY Pest & Exterminating, we understand the unique pest challenges faced by {area.name} residents and businesses. From subway-adjacent rodent issues to high-rise bed bug problems, our local expertise ensures effective, targeted solutions.
                  </p>
                  <p className="body-medium" style={{ color: 'var(--text-body)' }}>
                    Our eco-friendly Green Shield Approach is perfect for {area.name}'s diverse communities - safe for families, pets, restaurants, and commercial spaces while delivering guaranteed results.
                  </p>
                </div>
                
                <div>
                  <h2 className="heading-2 mb-4">Why {area.name} Trusts Us</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                      <div>
                        <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>Same-Day Service</h3>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Emergency response available</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                      <div>
                        <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>Licensed & Insured</h3>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Fully certified technicians</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                      <div>
                        <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>Eco-Friendly</h3>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Safe for children and pets</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                      <div>
                        <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>Satisfaction Guaranteed</h3>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Free return if pests come back</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h2 className="heading-2 mb-4">Services Available in {area.name}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {services.slice(0, 6).map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.id}`}
                        className="product-card card-hover-lift"
                      >
                        <h3 className="heading-3 mb-2" style={{ fontSize: '1rem' }}>{service.title}</h3>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {service.description}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h2 className="heading-2 mb-4">Common Pest Problems in {area.name}</h2>
                  <p className="body-medium mb-4" style={{ color: 'var(--text-body)' }}>
                    Like many New York communities, {area.name} experiences seasonal pest challenges:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--accent-text)' }}>•</span>
                      <span className="body-medium"><strong>Rodents (Mice & Rats):</strong> Year-round problem, especially in colder months</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--accent-text)' }}>•</span>
                      <span className="body-medium"><strong>Bed Bugs:</strong> Common in multi-unit buildings and high-traffic areas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--accent-text)' }}>•</span>
                      <span className="body-medium"><strong>Cockroaches:</strong> German and American varieties thrive in urban environments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--accent-text)' }}>•</span>
                      <span className="body-medium"><strong>Ants:</strong> Seasonal invasions during spring and summer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--accent-text)' }}>•</span>
                      <span className="body-medium"><strong>Termites:</strong> Silent destroyers that require professional treatment</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="product-card sticky top-24">
                <h3 className="heading-3 mb-4">Serving {area.name}</h3>
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Call now for same-day service in your neighborhood.
                </p>
                
                <div className="space-y-3 mb-6">
                  <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary w-full">
                    <Phone size={20} />
                    Call {companyInfo.phone}
                  </a>
                  <Link to="/contact" className="btn-secondary w-full">
                    Get Free Quote
                  </Link>
                </div>
                
                <div className="pt-6 border-t" style={{ borderColor: 'var(--border-light)' }}>
                  <h4 className="heading-3 mb-3" style={{ fontSize: '1rem' }}>Service Hours</h4>
                  <p className="body-small mb-2 font-semibold" style={{ color: 'var(--accent-text)' }}>Open 24 Hours</p>
                  <p className="body-small mb-4">Monday - Sunday</p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Always available for your pest control needs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Service Areas */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-2 mb-8 text-center">Other Service Areas</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {serviceAreas.filter(a => a.id !== id).map(otherArea => (
              <Link
                key={otherArea.id}
                to={`/areas/${otherArea.id}`}
                className="product-card card-hover-lift text-center"
              >
                <MapPin size={24} className="mx-auto mb-2" style={{ color: 'var(--accent-text)' }} />
                <h3 className="heading-3" style={{ fontSize: '1rem' }}>{otherArea.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AreaDetail;